'use client';
import React from 'react';
import type { ReactNode, Ref } from 'react';
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from 'react-aria-components';
import { cx } from '@milanote/roma/utils/cx';

const SEMANTIC_NAME_BASE = 'RomaCheckboxBase';
const SEMANTIC_NAME = 'RomaCheckbox';

export interface CheckboxBaseProps {
    size?: 'sm' | 'md';
    variant?: 'form' | 'task';
    className?: string;
    isFocusVisible?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    isIndeterminate?: boolean;
    isInvalid?: boolean;
}

/**
 * Tracks unchecked → checked transitions and returns `true` while the
 * bounce / flash animation classes should be applied.
 *
 * Key behaviours:
 * - Does **not** animate on mount (the ref is initialised to the current
 *   value, so the first effect run never sees a false → true change).
 * - Uses React state so the animation classes persist across unrelated
 *   react-aria rerenders (focus, pressed, etc.) that would otherwise
 *   remove transiently-computed classes before the CSS animation finishes.
 * - Clears only when the checkbox becomes unchecked, ensuring the one-shot
 *   CSS animation completes undisturbed.
 */
const useShouldAnimateCheckedState = (isChecked: boolean): boolean => {
    const [shouldAnimate, setShouldAnimate] = React.useState(false);
    const previousCheckedRef = React.useRef(isChecked);

    React.useLayoutEffect(() => {
        const wasUnchecked = !previousCheckedRef.current;
        previousCheckedRef.current = isChecked;

        if (wasUnchecked && isChecked) {
            setShouldAnimate(true);
        } else if (!isChecked) {
            setShouldAnimate(false);
        }
    }, [isChecked]);

    return shouldAnimate;
};

const styles = {
    common: {
        root: cx(
            // layout
            'relative flex shrink-0 cursor-pointer appearance-none items-center justify-center ring-1 ring-inset',
            // transitions
            'transition-[background-color,box-shadow] duration-100 ease-linear',
            // disabled state
            'data-[disabled]:cursor-not-allowed data-[disabled]:bg-disabled_subtle data-[disabled]:ring-disabled',
            // invalid state
            'data-[invalid]:ring-error_subtle',
            // focus ring
            'data-[focus-visible]:outline-2 data-[focus-visible]:-outline-offset-1 data-[focus-visible]:outline-focus-ring',
        ),
        icon: {
            base: 'pointer-events-none absolute',
            indeterminate: cx(
                // hidden by default
                'opacity-0',
                // opacity transition
                'transition-opacity duration-100',
                // visible when indeterminate
                'data-[indeterminate]:opacity-100',
            ),
            selected: cx(
                // visible by default
                'opacity-100',
                // hidden when indeterminate
                'data-[indeterminate]:opacity-0',
            ),
        },
    },
    sizes: {
        sm: {
            root: 'rounded-xxs size-4',
            icon: 'size-3',
        },
        md: {
            root: 'rounded-xs size-5',
            icon: 'size-3.5',
        },
    },
    variants: {
        form: {
            root: cx(
                // default state
                'bg-primary ring-primary',
                // checked state
                'data-[checked]:ring-transparent data-[checked]:bg-checkbox-fill_form',
            ),
            // checked flash animation
            checked_flash: 'animate-checkbox-fill-flash_form',
            icon: cx(
                // default icon color
                'text-utility-gray-50',
                // disabled icon color
                'data-[disabled]:text-fg-disabled',
            ),
        },
        task: {
            root: cx(
                // default state
                'bg-primary ring-primary_focused',
                // checked state
                'data-[checked]:ring-transparent data-[checked]:bg-checkbox-fill_task',
            ),
            // checked flash animation
            checked_flash: 'animate-checkbox-fill-flash_task',
            icon: cx(
                // default icon color
                'text-fg-disabled_subtle',
                // disabled icon color
                'data-[disabled]:text-fg-disabled',
            ),
        },
    },
};

export const CheckboxBase = ({
    className,
    isSelected,
    isDisabled,
    isIndeterminate,
    isInvalid,
    size = 'sm',
    variant = 'form',
    isFocusVisible = false,
}: CheckboxBaseProps) => {
    const isChecked = Boolean(isSelected || isIndeterminate);
    const shouldAnimateCheckedState = useShouldAnimateCheckedState(isChecked);

    return (
        <div className={cx(SEMANTIC_NAME_BASE, shouldAnimateCheckedState && 'animate-checkbox-bounce')}>
            {/* 👆 wrapper div provides target for bounce animation */}
            <div
                data-size={size}
                data-variant={variant}
                data-checked={isChecked || undefined}
                data-selected={isSelected || undefined}
                data-indeterminate={isIndeterminate || undefined}
                data-disabled={isDisabled || undefined}
                data-invalid={isInvalid || undefined}
                data-focus-visible={isFocusVisible || undefined}
                className={cx(
                    styles.common.root,
                    styles.sizes[size].root,
                    styles.variants[variant].root,
                    shouldAnimateCheckedState && styles.variants[variant].checked_flash,
                    !isChecked && 'delay-[50ms]',
                    className,
                )}
            >
                {/* Indeterminate line - no stroke animation */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 14 14"
                    fill="none"
                    data-indeterminate={isIndeterminate || undefined}
                    data-disabled={isDisabled || undefined}
                    className={cx(
                        styles.common.icon.base,
                        styles.common.icon.indeterminate,
                        styles.sizes[size].icon,
                        styles.variants[variant].icon,
                    )}
                >
                    <path
                        d="M2.91675 7H11.0834"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>

                {/* Checkmark tick - with stroke animation */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 14 14"
                    fill="none"
                    data-indeterminate={isIndeterminate || undefined}
                    data-disabled={isDisabled || undefined}
                    className={cx(
                        styles.common.icon.base,
                        styles.common.icon.selected,
                        styles.sizes[size].icon,
                        styles.variants[variant].icon,
                    )}
                >
                    <path
                        d="M2.33325 7L5.24992 9.91667L11.6666 3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="[stroke-dasharray:16]"
                        style={{
                            strokeDashoffset: isSelected ? 0 : 16,
                            transition: isSelected
                                ? 'stroke-dashoffset 150ms linear 150ms'
                                : 'stroke-dashoffset 100ms linear',
                        }}
                    />
                </svg>
            </div>
        </div>
    );
};
CheckboxBase.displayName = SEMANTIC_NAME_BASE;

export interface CheckboxProps extends AriaCheckboxProps {
    ref?: Ref<HTMLLabelElement>;
    size?: 'sm' | 'md';
    variant?: 'form' | 'task';
    label?: ReactNode;
    hint?: ReactNode;
}

export const Checkbox = ({
    label,
    hint,
    size = 'sm',
    variant = 'form',
    className,
    ...ariaCheckboxProps
}: CheckboxProps) => {
    const sizes = {
        sm: {
            root: 'gap-2',
            textWrapper: '',
            label: 'text-sm font-medium',
            hint: 'text-sm',
            checkbox: label || hint ? 'mt-0.5' : '',
        },
        md: {
            root: 'gap-3',
            textWrapper: 'gap-0.5',
            label: 'text-md font-medium',
            hint: 'text-md',
            checkbox: '',
        },
    };

    return (
        <AriaCheckbox
            {...ariaCheckboxProps}
            className={(state) =>
                cx(
                    SEMANTIC_NAME,
                    'flex items-start',
                    state.isDisabled && 'cursor-not-allowed',
                    sizes[size].root,
                    typeof className === 'function' ? className(state) : className,
                )
            }
            data-test-id={`checkbox-${ariaCheckboxProps.name || 'unnamed'}`}
        >
            {({ isSelected, isIndeterminate, isDisabled, isFocusVisible }) => (
                <>
                    <CheckboxBase
                        size={size}
                        variant={variant}
                        isSelected={isSelected}
                        isIndeterminate={isIndeterminate}
                        isDisabled={isDisabled}
                        isFocusVisible={isFocusVisible}
                        isInvalid={ariaCheckboxProps.isInvalid}
                        className={sizes[size].checkbox}
                    />
                    {(label || hint) && (
                        <div className={cx('inline-flex flex-col', sizes[size].textWrapper)}>
                            {label && (
                                <p
                                    className={cx(
                                        'select-none',
                                        sizes[size].label,
                                        ariaCheckboxProps.isInvalid ? 'text-error-primary' : 'text-secondary',
                                    )}
                                >
                                    {label}
                                </p>
                            )}
                            {hint && (
                                <span
                                    className={cx(
                                        sizes[size].hint,
                                        ariaCheckboxProps.isInvalid ? 'text-error-primary' : 'text-tertiary',
                                    )}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {hint}
                                </span>
                            )}
                        </div>
                    )}
                </>
            )}
        </AriaCheckbox>
    );
};
Checkbox.displayName = SEMANTIC_NAME;
